/**
 * Landing page sections — extracted from LandingPage.js so the page itself
 * can stay an ~80-line orchestrator. No business logic lives here — just
 * presentation. Auth + navigation handlers are passed in as props.
 */
import React from 'react';
import {
  ArrowRight, BookOpen, FileText, Map as MapIcon, Search, Sparkles,
  CheckSquare, Layers, Compass, Flag, Globe, Landmark, Scale, Cpu, ExternalLink,
  Check, Star, Quote,
} from 'lucide-react';
import { ATLAS_LOGO_URL, ATLAS_NAME, ATLAS_SUBTITLE, ATLAS_TAGLINE, ATLAS_SUBTAGLINE } from '@/lib/brand';

export const LANDING_FEATURES = [
  { icon: BookOpen, title: 'Standards-aligned lesson plans', desc: 'Every unit cross-referenced to KSDE HGSS and NCSS frameworks — no more spreadsheet alignment.' },
  { icon: FileText, title: 'Primary source library', desc: '957 annotated documents from every era — Mayflower Compact to contemporary digital archives.' },
  { icon: MapIcon, title: 'Interactive historical atlases', desc: 'GIS-grade overlays — slide through time on era-specific maps with borders, trade routes, and empires.' },
  { icon: Cpu, title: 'AI teaching assistant', desc: 'Generate lesson plans, rubrics, and primary-source excerpts — calibrated to your standards.' },
  { icon: CheckSquare, title: 'Auto-graded quizzes', desc: 'Vocabulary and identification quizzes with instant feedback and gradebook export.' },
  { icon: Layers, title: 'Skeleton notes & lectures', desc: 'Pre-populated student & teacher note packets, plus interactive lecture presentation mode.' },
];

export const LANDING_SUBJECTS = [
  { icon: Flag, label: 'US History', desc: 'Founding documents through modern America' },
  { icon: Globe, label: 'World History', desc: 'Ancient civilizations to globalization' },
  { icon: Landmark, label: 'Civics', desc: 'Rights, participation, citizenship' },
  { icon: Scale, label: 'Government', desc: 'Branches, federalism, policy' },
];

// Testimonials — real teacher quotes provided by the owner.
export const LANDING_TESTIMONIALS = [
  {
    quote: 'Atlas History is an outstanding resource for Kansas social studies teachers. Every lesson and activity is intentionally aligned with the Kansas HGSS Standards. It is especially valuable for new teachers who are developing a rigorous, engaging curriculum that challenges students and establishes high expectations.',
    name: 'Wallace Pursely',
    role: 'Principal',
    initials: 'WP',
  },
  {
    quote: 'The compelling questions within the CBA State Assessment resources are relevant and engaging. With four questions available for every grade level, teachers can expose students to the state assessment model throughout the year without the activities feeling like test preparation. Students build the skills they need while remaining focused on meaningful historical inquiry.',
    name: 'Isabella Torres',
    role: 'Ancient Civilizations',
    initials: 'IT',
  },
  {
    quote: 'Atlas History saves teachers valuable planning time by providing organized, standards-aligned resources that are ready to use while remaining easy to adapt. The AI question generator is especially useful — teachers can enter any topic or current event and instantly create meaningful bellwork, discussion questions, or exit tickets. It allows teachers to spend less time building materials from scratch and more time focusing on student learning.',
    name: 'Robert Metzger',
    role: 'Civics and American Government',
    initials: 'RM',
  },
];

const GoogleSVG = ({ light = false }) => (
  <svg viewBox="0 0 24 24" className="h-4 w-4">
    <path fill={light ? '#fff' : '#4285F4'} d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill={light ? '#fff' : '#34A853'} d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z" />
    <path fill={light ? '#fff' : '#FBBC05'} d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.93l3.66-2.83z" />
    <path fill={light ? '#fff' : '#EA4335'} d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

export function LandingNav({ onTour, onAbout, onSignIn }) {
  return (
    <nav className="sticky top-0 z-40 bg-[#FDFBF7]/85 backdrop-blur-md border-b border-[#EBE7E0]">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2.5" data-testid="landing-brand">
          <div className="w-10 h-10 rounded-md bg-white border border-[#EBE7E0] flex items-center justify-center overflow-hidden">
            <img src={ATLAS_LOGO_URL} alt="" className="w-full h-full object-contain" />
          </div>
          <div className="flex flex-col leading-tight text-left">
            <span className="font-serif-display text-lg text-[#14213D] tracking-tight">{ATLAS_NAME}</span>
            <span className="text-[11px] tracking-[0.18em] uppercase text-[#5A6577]">{ATLAS_SUBTITLE}</span>
          </div>
        </button>
        <div className="flex items-center gap-3">
          <button onClick={onTour} className="hidden sm:inline-flex text-sm text-[#14213D] hover:text-[#1D2D50] px-3 py-1.5" data-testid="landing-tour">Tour</button>
          <button onClick={onAbout} className="hidden sm:inline-flex text-sm text-[#14213D] hover:text-[#1D2D50] px-3 py-1.5" data-testid="landing-about">About</button>
          <button onClick={onSignIn} className="inline-flex items-center gap-1.5 bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7] text-sm font-medium px-4 py-2 rounded-md transition-colors" data-testid="landing-signin">
            Sign in <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </nav>
  );
}

/**
 * LandingHero — two-column redesign.
 *   Left  : brand mark → overline → h1 → sub → 1 primary CTA + tour text link → accessible search button
 *   Right : bordered/rounded/shadowed product screenshot of the Grade 10 dashboard
 * Stacks on mobile.
 */
export function LandingHero({ stats, onGoogle, onSignIn, onTour }) {
  return (
    <header className="relative overflow-hidden bg-[#FDFBF7]">
      {/* Very soft parchment texture — kept subtle so screenshot stays crisp */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 25% 10%, rgba(20,33,61,0.10) 0, transparent 55%), radial-gradient(circle at 85% 90%, rgba(212,175,55,0.10) 0, transparent 45%)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 py-12 sm:py-16 md:py-20 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-10 lg:gap-14 items-center">
        {/* LEFT: copy + CTA */}
        <div className="text-center lg:text-left order-2 lg:order-1">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto lg:mx-0 mb-4 flex items-center justify-center" data-testid="hero-logo">
            <img src={ATLAS_LOGO_URL} alt="Atlas History" className="w-full h-full object-contain drop-shadow-sm" />
          </div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3" data-testid="hero-overline">
            {ATLAS_SUBTAGLINE}
          </p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#14213D] tracking-tight leading-[1.05] mb-4 sm:mb-5">
            {ATLAS_TAGLINE}
          </h1>
          <p className="text-base sm:text-lg text-[#4A5568] max-w-xl mx-auto lg:mx-0 leading-relaxed mb-7 sm:mb-8">
            {stats.units} standards-aligned units. {stats.sources} primary sources. Interactive atlases, AI assistance, and a search bar that finds the right resource in seconds.
          </p>

          {/* Single primary CTA + demoted tour link */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-center lg:justify-start">
            <button
              onClick={onGoogle}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7] text-sm font-medium px-6 py-3 rounded-md transition-colors shadow-sm"
              data-testid="hero-google-signin-btn"
            >
              <GoogleSVG light />
              Continue with Google
            </button>
            <button
              onClick={onTour}
              className="inline-flex items-center justify-center gap-1.5 text-sm text-[#14213D] font-medium underline decoration-[#D4AF37] decoration-2 underline-offset-[6px] hover:decoration-[#14213D] transition-colors"
              data-testid="hero-tour-link"
            >
              <Sparkles className="h-4 w-4 text-[#D4AF37]" /> Take the tour
            </button>
          </div>

          {/* Accessible search button (keyboard + screen-reader friendly) */}
          <div className="max-w-2xl mx-auto lg:mx-0 mt-8 sm:mt-10">
            <button
              type="button"
              onClick={onSignIn}
              className="group w-full text-left bg-white border border-[#EBE7E0] rounded-full pl-11 sm:pl-12 pr-4 py-3 text-sm text-[#5A6577] shadow-sm flex items-center hover:border-[#14213D]/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14213D]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FDFBF7] transition-colors relative"
              aria-label="Search lessons, eras, standards, people, and primary sources — sign in to continue"
              data-testid="search-teaser"
            >
              <Search className="absolute left-4 h-4 w-4 text-[#5A6577] flex-shrink-0" aria-hidden="true" />
              <span className="truncate">
                <span className="sm:hidden">Search lessons, eras, sources&hellip;</span>
                <span className="hidden sm:inline">Search lessons, eras, standards, people, primary sources&hellip;</span>
              </span>
              <kbd className="hidden sm:inline-block ml-auto flex-shrink-0 px-1.5 py-0.5 bg-[#F9F8F4] border border-[#EBE7E0] rounded text-[11px] text-[#5A6577] font-mono">⌘K</kbd>
            </button>
          </div>
        </div>

        {/* RIGHT: bordered product screenshot */}
        <div className="order-1 lg:order-2">
          <div className="relative">
            {/* Accent — soft gold glow behind the frame */}
            <div
              aria-hidden="true"
              className="absolute -inset-6 lg:-inset-8 rounded-[28px] bg-gradient-to-br from-[#D4AF37]/15 via-transparent to-[#14213D]/10 blur-2xl"
            />
            <div className="relative rounded-2xl border border-[#EBE7E0] bg-white shadow-[0_20px_50px_-15px_rgba(20,33,61,0.35)] overflow-hidden ring-1 ring-black/[0.04]">
              {/* Faux browser chrome */}
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-[#EBE7E0] bg-[#F9F8F4]">
                <span className="w-2.5 h-2.5 rounded-full bg-[#E9D3D3]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#EFE1BA]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#D1E0D2]" />
                <span className="ml-3 text-[11px] tracking-wider text-[#5A6577] font-mono">atlashistory.org / dashboard / grade 10</span>
              </div>
              <picture>
                <source srcSet="/landing/dashboard-preview.webp" type="image/webp" />
                <img
                  src="/landing/dashboard-preview.jpg"
                  alt="Atlas History dashboard preview showing 12 Grade 10 Modern World History units"
                  className="block w-full h-auto"
                  loading="eager"
                  decoding="async"
                  data-testid="hero-dashboard-preview"
                />
              </picture>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export function LandingStats({ stats }) {
  return (
    <section className="bg-[#14213D] text-[#FDFBF7] border-y border-black/20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 grid grid-cols-3 gap-3 sm:gap-6 text-center">
        {[
          { k: stats.units, v: 'Curriculum units' },
          { k: stats.sources, v: 'Primary sources' },
          { k: stats.grades, v: 'Grade bands' },
        ].map((s) => (
          <div key={s.v}>
            <p className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[#D4AF37] leading-none">{s.k}</p>
            <p className="text-[11px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em] text-[#FDFBF7]/75 mt-2 sm:mt-3">{s.v}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function LandingSubjects() {
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-6 py-14 sm:py-20">
      <div className="text-center mb-10 sm:mb-12">
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Browse by subject</p>
        <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl text-[#14213D] tracking-tight">Four disciplines, one workspace.</h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {LANDING_SUBJECTS.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-[#F9F8F4] border border-[#EBE7E0] rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow" data-testid={`landing-subject-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <Icon className="h-6 w-6 text-[#7A93AC] mb-3 sm:mb-4" strokeWidth={1.5} />
              <h3 className="font-serif-display text-lg sm:text-xl text-[#14213D] mb-2">{s.label}</h3>
              <p className="text-xs sm:text-sm text-[#5A6577] leading-relaxed">{s.desc}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function LandingFeatures() {
  return (
    <section className="bg-[#F9F8F4] border-y border-[#EBE7E0]">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 py-14 sm:py-20">
        <div className="text-center mb-10 sm:mb-14">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">What&apos;s inside</p>
          <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl text-[#14213D] tracking-tight">Built around the actual workflow of a teacher.</h2>
          <p className="text-sm text-[#4A5568] max-w-2xl mx-auto mt-4 leading-relaxed">Less hunting, more teaching. Every resource is cross-linked to standards, eras, and grade levels — so the relevant tool is always one click away.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {LANDING_FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <article key={f.title} className="bg-white border border-[#EBE7E0] rounded-md p-5 sm:p-6 hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`landing-feature-${f.title.toLowerCase().replace(/\s+/g, '-').slice(0, 40)}`}>
                <Icon className="h-5 w-5 text-[#14213D] mb-3 sm:mb-4" strokeWidth={1.5} />
                <h3 className="font-serif-display text-lg text-[#14213D] mb-2 leading-tight">{f.title}</h3>
                <p className="text-sm text-[#5A6577] leading-relaxed">{f.desc}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * LandingPricing — three cards driven by the live Stripe tier config.
 *   Preview (Free)    — sourced from client-side fixed copy (no Stripe row)
 *   Teacher (featured) — Pro Monthly from Stripe (all tools)
 *   Department (Custom) — email sales, no Stripe row
 *
 * The `tiers` prop is the raw `{tiers: {...}}` object returned by
 * GET /api/subscription/tiers so we don't invent numbers.
 */
export function LandingPricing({ tiers, onSignIn, onGoogle, onContact }) {
  const pro = tiers?.pro_monthly;
  const basic = tiers?.basic_monthly;
  const proPrice = pro?.price != null ? `$${Math.round(pro.price)}` : '$25';
  const basicPrice = basic?.price != null ? `$${Math.round(basic.price)}` : '$10';

  const plans = [
    {
      key: 'preview',
      name: 'Preview',
      badge: null,
      description: 'Browse the archive and see how a unit is laid out — no card required.',
      price: 'Free',
      priceSub: 'forever',
      features: [
        'Full landing archive + subject browse',
        'Full preview unit — Grade 9 · Ancient Mesopotamia',
        'View standards alignment',
        'Google sign-in',
      ],
      cta: 'Continue with Google',
      onClick: onGoogle,
      featured: false,
    },
    {
      key: 'teacher',
      name: 'Teacher',
      badge: 'Most popular',
      description: pro?.description || 'Everything a solo classroom teacher needs — every unit, every tool, every era.',
      price: proPrice,
      priceSub: `${pro?.interval || 'month'} · billed via Stripe`,
      features: [
        'All 78 curriculum units, all grade bands',
        'Skeleton notes, lecture mode, rubrics',
        'AI lesson & rubric generator',
        'Print-to-PDF with branded cover page',
      ],
      cta: 'Start with Teacher',
      onClick: onSignIn,
      featured: true,
      // Show the smaller Basic tier as a "starts at" hint
      hint: basic ? `or ${basicPrice}/mo for browse-only (Basic)` : null,
    },
    {
      key: 'department',
      name: 'Department',
      badge: null,
      description: 'For social-studies departments and district-wide rollouts.',
      price: 'Custom',
      priceSub: 'annual seat license',
      features: [
        'Bulk seats + shared admin console',
        'District-wide standards mapping',
        'Onboarding & PD session',
        'Priority support from Mr. Casteel',
      ],
      cta: 'Contact us',
      onClick: onContact,
      featured: false,
    },
  ];

  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-6 py-14 sm:py-20" id="pricing">
      <div className="text-center mb-10 sm:mb-14">
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Pricing</p>
        <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl text-[#14213D] tracking-tight">One classroom, or a whole department.</h2>
        <p className="text-sm text-[#4A5568] max-w-2xl mx-auto mt-4 leading-relaxed">
          Same archive, same tools — pick the seat that fits. Cancel anytime.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 items-stretch">
        {plans.map((p) => {
          const isFeat = p.featured;
          return (
            <div
              key={p.key}
              data-testid={`pricing-card-${p.key}`}
              className={[
                'relative flex flex-col rounded-md border shadow-sm transition-all',
                isFeat
                  ? 'bg-[#14213D] text-[#FDFBF7] border-[#14213D] shadow-[0_16px_38px_-16px_rgba(20,33,61,0.55)] md:-translate-y-2'
                  : 'bg-white text-[#14213D] border-[#EBE7E0] hover:shadow-md',
                'p-6 sm:p-7',
              ].join(' ')}
            >
              {p.badge && (
                <span
                  className="absolute -top-3 left-6 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#D4AF37] text-[#14213D] text-[11px] uppercase tracking-[0.16em] font-semibold shadow"
                  data-testid={`pricing-badge-${p.key}`}
                >
                  <Sparkles className="h-3 w-3" /> {p.badge}
                </span>
              )}

              <div className="mb-4">
                <p className={`text-[11px] uppercase tracking-[0.24em] font-semibold mb-1 ${isFeat ? 'text-[#D4AF37]' : 'text-[#D4AF37]'}`}>
                  {p.name}
                </p>
                <p className={`text-sm leading-relaxed ${isFeat ? 'text-[#FDFBF7]/80' : 'text-[#4A5568]'}`}>
                  {p.description}
                </p>
              </div>

              <div className="mb-5">
                <div className="flex items-baseline gap-2">
                  <span className={`font-serif-display text-4xl leading-none tracking-tight ${isFeat ? 'text-[#FDFBF7]' : 'text-[#14213D]'}`}>
                    {p.price}
                  </span>
                  {p.priceSub && (
                    <span className={`text-xs ${isFeat ? 'text-[#FDFBF7]/70' : 'text-[#5A6577]'}`}>
                      / {p.priceSub}
                    </span>
                  )}
                </div>
                {p.hint && (
                  <p className={`text-xs mt-1 ${isFeat ? 'text-[#FDFBF7]/60' : 'text-[#5A6577]'}`}>
                    {p.hint}
                  </p>
                )}
              </div>

              <ul className="space-y-2 mb-6 flex-1">
                {p.features.map((feat) => (
                  <li key={feat} className={`flex items-start gap-2 text-sm ${isFeat ? 'text-[#FDFBF7]/90' : 'text-[#2D3748]'}`}>
                    <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isFeat ? 'text-[#D4AF37]' : 'text-[#14213D]'}`} />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={p.onClick}
                className={[
                  'inline-flex items-center justify-center gap-2 w-full text-sm font-medium px-4 py-2.5 rounded-md transition-colors',
                  isFeat
                    ? 'bg-[#D4AF37] text-[#14213D] hover:bg-[#C19E2C]'
                    : 'border border-[#14213D] text-[#14213D] hover:bg-[#F9F8F4]',
                ].join(' ')}
                data-testid={`pricing-cta-${p.key}`}
              >
                {p.cta} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * LandingTestimonials — 3 teacher quote cards + a navy "Made by Mr. Casteel"
 * maker strip that reuses the About page's profile photo (/about/profile.jpg).
 * Quotes are real teacher endorsements (see LANDING_TESTIMONIALS above).
 */
export function LandingTestimonials({ onAbout }) {
  return (
    <section className="bg-[#F9F8F4] border-y border-[#EBE7E0]">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 py-14 sm:py-20">
        <div className="text-center mb-10 sm:mb-12">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Loved by teachers</p>
          <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl text-[#14213D] tracking-tight">Built with the classroom in the room.</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {LANDING_TESTIMONIALS.map((t, i) => (
            <article
              key={`${t.name}-${i}`}
              className="bg-white border border-[#EBE7E0] rounded-md p-5 sm:p-6 shadow-sm flex flex-col"
              data-testid={`testimonial-card-${i}`}
            >
              <div className="flex items-center gap-1 mb-3" aria-label="5 star rating">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} className="h-3.5 w-3.5 fill-[#D4AF37] text-[#D4AF37]" strokeWidth={1.5} />
                ))}
              </div>
              <Quote className="h-5 w-5 text-[#D4AF37]/60 mb-2" strokeWidth={1.5} />
              <p className="text-sm text-[#2D3748] leading-relaxed flex-1">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="mt-5 pt-4 border-t border-[#EBE7E0] flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#14213D] text-[#D4AF37] flex items-center justify-center text-xs font-semibold">
                  {t.initials}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#14213D] truncate">{t.name}</p>
                  <p className="text-xs text-[#5A6577] truncate">{t.role}</p>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Maker strip */}
        <div
          className="mt-10 sm:mt-14 rounded-lg overflow-hidden bg-[#14213D] text-[#FDFBF7] shadow-sm border border-black/20"
          data-testid="maker-strip"
        >
          <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] items-stretch">
            {/* Photo slot — reuses the About page headshot */}
            <div className="relative bg-[#0F1A30] flex items-center justify-center min-h-[220px] md:min-h-0">
              <img
                src="/about/profile.jpg"
                alt="Mr. Chase A. Casteel — Atlas History creator"
                className="w-full h-full object-cover object-[center_20%]"
                data-testid="maker-photo"
              />
              {/* subtle gold rule accent */}
              <span aria-hidden="true" className="absolute inset-y-0 right-0 w-[3px] bg-[#D4AF37]" />
            </div>
            <div className="p-6 sm:p-8">
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-semibold mb-2">
                Made by a teacher
              </p>
              <h3 className="font-serif-display text-2xl sm:text-3xl text-[#FDFBF7] tracking-tight mb-3">
                Mr. Chase A. Casteel
              </h3>
              <p className="text-sm text-[#FDFBF7]/85 leading-relaxed max-w-2xl">
                Atlas History was built during nights and prep periods by a full-time high-school social studies teacher who wanted every unit, primary source, and lesson plan in one place — indexed against KSDE HGSS &amp; NCSS. Every feature you use was field-tested in a real classroom in Flint Hills, Kansas.
              </p>
              <button
                onClick={onAbout}
                className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#D4AF37] hover:text-[#FDFBF7] font-medium underline decoration-[#D4AF37]/50 decoration-2 underline-offset-[6px] hover:decoration-[#FDFBF7] transition-colors"
                data-testid="maker-about-link"
              >
                Read the full story <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function LandingCTA({ onGoogle, onPricing }) {
  return (
    <section className="max-w-4xl mx-auto px-5 sm:px-6 py-14 sm:py-20 text-center">
      <Compass className="h-8 w-8 text-[#D4AF37] mx-auto mb-5 sm:mb-6" strokeWidth={1.25} />
      <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl text-[#14213D] tracking-tight mb-3 sm:mb-4">Ready when you are.</h2>
      <p className="text-sm text-[#4A5568] max-w-xl mx-auto mb-8 leading-relaxed">Sign in, browse the dashboard, and the next lesson is already laid out — annotated sources, exit ticket, and rubric ready to project.</p>
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-center gap-3 max-w-md sm:max-w-none mx-auto">
        <button onClick={onGoogle} className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7] text-sm font-medium px-6 py-3 rounded-md transition-colors" data-testid="cta-google-signin">
          <GoogleSVG light />
          Continue with Google
        </button>
        <button onClick={onPricing} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-[#14213D] text-[#14213D] hover:bg-[#F9F8F4] text-sm font-medium px-6 py-3 rounded-md transition-colors" data-testid="cta-pricing">
          View pricing
        </button>
      </div>
    </section>
  );
}

export function LandingFooter({ onTour, onPricing, onSignIn, onAbout, onToolkit, onPrivacy, onTerms, onContact }) {
  return (
    <footer className="bg-[#14213D] text-[#FDFBF7]/85 border-t border-black/30">
      <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 sm:grid-cols-4 gap-8 text-sm">
        <div className="sm:col-span-2">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 rounded-md bg-white flex items-center justify-center overflow-hidden">
              <img src={ATLAS_LOGO_URL} alt="" className="w-full h-full object-contain" />
            </div>
            <span className="font-serif-display text-lg text-[#FDFBF7] tracking-tight">{ATLAS_NAME}</span>
          </div>
          <p className="text-xs leading-relaxed max-w-sm">A modern editorial archive of standards-aligned curriculum resources for history, civics, government and social studies teachers.</p>
          <p className="text-[11px] italic mt-4 text-[#FDFBF7]/60">Made by Mr. Chase A. Casteel</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#FDFBF7]/70 mb-3 font-semibold">Platform</p>
          <ul className="space-y-2 text-xs">
            <li><button onClick={onTour} className="hover:text-[#FDFBF7]">Tour</button></li>
            <li><button onClick={onPricing} className="hover:text-[#FDFBF7]">Pricing</button></li>
            <li><button onClick={onSignIn} className="hover:text-[#FDFBF7]">Sign in</button></li>
          </ul>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#FDFBF7]/70 mb-3 font-semibold">Resources</p>
          <ul className="space-y-2 text-xs">
            <li><button onClick={onAbout} className="hover:text-[#FDFBF7]">About the Developer</button></li>
            <li><button onClick={onToolkit} className="hover:text-[#FDFBF7]">Teacher Toolkit</button></li>
            <li><button onClick={onContact} className="hover:text-[#FDFBF7]" data-testid="footer-contact-link-resources">Contact Us</button></li>
            <li>
              <a href="https://www.ksde.gov/student-success/resources-by-subject-area/history-government-and-social-studies" target="_blank" rel="noopener noreferrer" className="hover:text-[#FDFBF7] inline-flex items-center gap-1">
                KSDE Standards <ExternalLink className="h-3 w-3 opacity-60" />
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 text-[11px] text-[#FDFBF7]/60 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          <span>© {new Date().getFullYear()} Atlas History · An editorial archive for teachers</span>
          <button onClick={onPrivacy} className="hover:text-[#FDFBF7] underline-offset-2 hover:underline" data-testid="footer-privacy-link">Privacy</button>
          <button onClick={onContact} className="hover:text-[#FDFBF7] underline-offset-2 hover:underline" data-testid="footer-contact-link">Contact</button>
          <button onClick={onTerms} className="hover:text-[#FDFBF7] underline-offset-2 hover:underline" data-testid="footer-terms-link">Terms</button>
        </div>
      </div>
    </footer>
  );
}
